import { traducirError, useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, useAvisos, Ventana } from '@tallerya/ui';
import { useState } from 'react';
import { useCambiosSucursal, type Sucursal } from './datos';

// «Sucursales - Eliminar sucursal» del Figma. Confirma antes de la baja, y dice
// qué pasa con lo que ya existe: la baja es lógica, las órdenes se conservan.
//
// Se abre con una sucursal y se cierra con `null`. Si la API la rechaza —la
// última del taller, o la principal que alguien acaba de nombrar desde otra
// pantalla—, el motivo se dice acá adentro y la ventana sigue abierta.

interface Props {
  /** Solo lo que dice el texto. Sirve la fila del listado y la ficha de la edición. */
  sucursal: Pick<Sucursal, 'id' | 'name' | 'code'> | null;
  onCerrar: () => void;
  /** Después de eliminarla. La edición vuelve al listado; el listado no hace nada más. */
  onEliminada?: () => void;
}

export function EliminarSucursal({ sucursal, onCerrar, onEliminada }: Props) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosSucursal();
  const [eliminando, setEliminando] = useState(false);
  const [problema, setProblema] = useState<string | null>(null);

  function cerrar() {
    if (eliminando) return;
    setProblema(null);
    onCerrar();
  }

  async function eliminar() {
    if (!sucursal) return;
    setEliminando(true);
    setProblema(null);
    try {
      const error = await cambios.eliminar(String(sucursal.id));
      if (error) {
        setProblema(error.code ? traducirError(error.code, t) : t('ui.formulario.aviso.sinRespuesta'));
        return;
      }
    } catch {
      // Sin red la petición lanza; sin esto «Cancelar» quedaba apagado para siempre (#124).
      setProblema(t('ui.formulario.aviso.sinRespuesta'));
      return;
    } finally {
      setEliminando(false);
    }
    avisar({ titulo: t('admin.sucursales.aviso.eliminada', { nombre: sucursal.name }) });
    onCerrar();
    onEliminada?.();
  }

  return (
    <Ventana
      abierta={sucursal !== null}
      titulo={t('admin.sucursales.eliminar.titulo')}
      descripcion={
        sucursal && t('admin.sucursales.eliminar.texto', { nombre: sucursal.name, codigo: sucursal.code })
      }
      acciones={
        <>
          <Boton variante="texto" disabled={eliminando} onClick={cerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton variante="peligro" disabled={eliminando} aria-busy={eliminando} onClick={() => void eliminar()}>
            {t('admin.sucursales.eliminar.boton')}
          </Boton>
        </>
      }
    >
      {problema && <AvisoError aviso={{ titulo: t('admin.sucursales.error.titulo'), texto: problema }} />}
    </Ventana>
  );
}
